import './styles.scss';

import { Button } from '@ets-global/b2c-website-ui';
import { fromJS } from 'immutable';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Title } from '../../../components/Element';
import CheckoutSummaryLayout from '../../../components/Layout/BuyingProcess/Session/CheckoutSummaryLayout';
import PrepToolsCarouselSkeleton from '../../../components/PrepToolsCarousel/PrepToolsCarouselSkeleton';
import SkeletonItem from '../../../components/SkeletonItem';
import PrepToolsList from '../../../containers/PrepToolsInCart/PrepToolsList';
import { useDataLayer, useRouter } from '../../../hooks';
import { paths } from '../../../paths';

const SessionCrossSelling = ({
  openSession,
  productsAndServices,
  purchase,
  sessionLoading,
  purchaseLoading,
  purchaseRefetch,
}) => {
  const { t } = useTranslation();

  const {
    generatePath,
    currentRoute: { params },
  } = useRouter();
  const { pushEventLoadedPurchaseFunnel } = useDataLayer();

  useEffect(() => {
    pushEventLoadedPurchaseFunnel({
      type: 'test order',
      stepName: 'cross sell',
      stepNumber: 3,
    });
  }, []);

  const session = fromJS(openSession || {});

  let preparationToolOnline = null;
  let preparationToolBook = null;

  const isPrepToolAvailable = (preparationTool) => {
    if (!purchase.voucher || !purchase.voucher.isCpf) {
      return true;
    }

    const purchaseLine = purchase.purchaseLines.filter(
      (purchaseLine) => purchaseLine.articleStock.id === parseInt(preparationTool.get('articleStockId'), 10)
    )[0];

    if (!purchaseLine) {
      return false;
    }

    return purchase.voucher.prepToolIds.includes(purchaseLine.articleStock.id);
  };

  if (session.has('preparationToolVersions') && !purchaseLoading) {
    preparationToolOnline = session
      .get('preparationToolVersions')
      .filter((preparationTool) => preparationTool.getIn(['relatedFamily', 'type']) === 'Online preparation tool')
      .filter((preparationTool) => isPrepToolAvailable(preparationTool));

    preparationToolBook = session
      .get('preparationToolVersions')
      .filter((preparationTool) => preparationTool.getIn(['relatedFamily', 'type']) === 'Book')
      .filter((preparationTool) => isPrepToolAvailable(preparationTool));
  }

  const loading = purchaseLoading || sessionLoading;

  return (
    <CheckoutSummaryLayout
      session={session}
      purchase={purchase}
      prevLinkProps={{
        to: generatePath(paths.SESSION_REGISTER_BY_REGISTRATION_ID, {
          sessionId: params.sessionId,
          registrationId: params.registrationId,
        }),
      }}
      className={'candidate'}
      productsAndServices={productsAndServices}
      loading={loading}
    >
      <div className="checkout-summary-layout__heading">
        <div>
          <Title lvl={2} className="checkout-summary-layout__title">
            <Trans>session-registration.cross-selling.title</Trans>
          </Title>
          <p className="checkout-summary-layout__subtitle">
            <Trans>session-registration.cross-selling.subtitle</Trans>
          </p>
        </div>
        {!loading && (
          <Button
            to={paths.PURCHASE_SUMMARY}
            params={{ registrationId: params.registrationId, sessionId: params.sessionId }}
            data-cypress="buying-process.next-step"
            aria-label={t('session-registration.cross-selling.finalize')}
          >
            <Trans>session-registration.cross-selling.finalize</Trans>
          </Button>
        )}
      </div>

      {loading && (
        <div className="cross-selling__block">
          <SkeletonItem className="cross-selling__block-title" style={{ height: '22px', width: '45%' }} />
          <SkeletonItem className="cross-selling__block-subtitle" style={{ height: '20px', width: '70%' }} />
          <div>
            <PrepToolsCarouselSkeleton nbItems={2} />
          </div>
        </div>
      )}
      {!loading && (
        <Fragment>
          {preparationToolOnline.size > 0 && (
            <div className="cross-selling__block">
              <h3 className="cross-selling__block-title">
                <Trans>prep-tool.online-tools.title</Trans>
              </h3>
              <p className="cross-selling__block-subtitle">
                <Trans>prep-tool.online-tools.subtitle</Trans>
              </p>
              <PrepToolsList
                prepTools={preparationToolOnline}
                registrationId={params.registrationId}
                purchase={purchase}
                itemsDisplay={2}
                className={'cross-selling__slider'}
                purchaseRefetch={purchaseRefetch}
              />
            </div>
          )}

          {preparationToolBook.size > 0 && (
            <div className="cross-selling__block">
              <h3 className="cross-selling__block-title">
                <Trans>prep-tool.books.title</Trans>
              </h3>
              <p className="cross-selling__block-subtitle">
                <Trans>prep-tool.books.subtitle</Trans>
              </p>
              <PrepToolsList
                prepTools={preparationToolBook}
                registrationId={params.registrationId}
                purchase={purchase}
                itemsDisplay={2}
                className={'cross-selling__slider'}
                purchaseRefetch={purchaseRefetch}
              />
            </div>
          )}

          {preparationToolBook.size === 0 &&
            preparationToolOnline.size === 0 &&
            purchase.voucher &&
            purchase.voucher.isCpf && <Trans>prep-tool.voucher-cpf.prep-tool-not-included</Trans>}
        </Fragment>
      )}
    </CheckoutSummaryLayout>
  );
};

SessionCrossSelling.propTypes = {
  openSession: PropTypes.object,
  purchase: PropTypes.object,
};

export default SessionCrossSelling;
